/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';
import {getPayload} from 'payload';

import {SeoHead} from 'UI/components/layout/SeoHead';
import {Headline, P} from 'UI/components/layout/Text';
import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';

import payloadConfig from '@payload-config';
import {DEFAULT_LOCALE} from 'GlobalShared/locales';
import {buildResponsiveImageData} from 'Shared/buildResponsiveImageData';
import {useBlogIndexPage} from 'UI/hooks/useBlogIndexPage';
import {AppLayout} from 'UI/layouts/AppLayout';

import {CONTENT_POPULATE} from 'Payload/utils/contentPopulate';
import {getLegalPages} from 'Payload/utils/getLegalPages';
import {buildBlogListJsonLd, buildBreadcrumbJsonLd, buildCollectionPageJsonLd, resolveImageUrl} from 'UI/utils/jsonLd';

import type {GetStaticProps, InferGetStaticPropsType} from 'next';
import type {Payload} from 'payload';
import type {NextSSGPageWithLayout} from 'types/global';
import type {BlogPost, Config, LegalPage} from 'types/payload-types';

const REVALIDATE_SECONDS = 18000;
/** Breite der Card-Vorschaubilder in Pixeln für die responsive Bildberechnung. */
const CARD_IMAGE_WIDTH = 600;

interface BlogIndexProps {
    legalPages: LegalPage[];
    posts: BlogPost[];
}

/**
 * Blog-Übersicht mit allen veröffentlichten Beiträgen, neueste zuerst.
 *
 * @param props       Die Seiten-Props.
 * @param props.posts Array der veröffentlichten Blog-Beiträge.
 * @returns ReactNode der Blog-Übersicht.
 */
const BlogIndexView: NextSSGPageWithLayout<typeof getStaticProps> = (
    {posts}: InferGetStaticPropsType<typeof getStaticProps>
) => {
    const {firstHeaderImage, t} = useBlogIndexPage(posts);

    return (
        <Page data-cy="BlogIndexView">
            <SeoHead
                description={t('blog.metaDescription')}
                image={resolveImageUrl(firstHeaderImage)}
                jsonLd={[
                    buildBlogListJsonLd(posts),
                    buildCollectionPageJsonLd(
                        {
                            name: t('blog.title'),
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/blog`
                        },
                        `${process.env.NEXT_PUBLIC_BASE_URL}/blog#itemlist`
                    ),
                    buildBreadcrumbJsonLd([
                        {
                            name: 'Home',
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/`
                        },
                        {
                            name: t('blog.title'),
                            url: `${process.env.NEXT_PUBLIC_BASE_URL}/blog`
                        }
                    ])
                ]}
                title={t('blog.title')}
                url={`${process.env.NEXT_PUBLIC_BASE_URL}/blog`}
            />
            <Headline $size="headline" as="h1">{t('blog.title')}</Headline>
            <Grid>
                {posts.map(post => {
                    const headerImage = typeof post.headerImage === 'object' ? post.headerImage : null;
                    const imgData = buildResponsiveImageData(headerImage, CARD_IMAGE_WIDTH);
                    const author = typeof post.author === 'object' ? post.author : null;

                    return (
                        <Card key={post.id} data-cy={`BlogIndexView-card-${post.slug}`}>
                            <Link href={`/blog/${post.slug}`}>
                                {imgData && (
                                    <CardImageWrapper>
                                        <ResponsiveImage
                                            alt={imgData.alt}
                                            focalX={imgData.focalX}
                                            focalY={imgData.focalY}
                                            sizes="(max-width: 600px) 100vw, 50vw"
                                            src={imgData.src}
                                            srcSet={imgData.srcSet}
                                        />
                                    </CardImageWrapper>
                                )}
                                <CardContent>
                                    <CardTitle $size="title" as="h2">{post.title}</CardTitle>
                                    <P $size="body">{post.description}</P>
                                    {author && <CardAuthor $size="labelMedium" as="span">{author.name}</CardAuthor>}
                                </CardContent>
                            </Link>
                        </Card>
                    );
                })}
            </Grid>
        </Page>
    );
};

BlogIndexView.displayName = 'BlogIndexView';

/**
 * `getLayout` wraps die Seite mit dem App-Shell-Layout.
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AppLayout.
 */
BlogIndexView.getLayout = (_route, pageProps, PageComponent) => (
    <AppLayout activeTab="blog" legalPages={pageProps.legalPages} isBackVisible realBackLink>
        <PageComponent {...pageProps} />
    </AppLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
BlogIndexView.getLayoutKey = () => 'blog';

export default BlogIndexView;

// ── Static Generation ───────────────────────────────────────

/**
 * Lädt alle veröffentlichten Blog-Beiträge, neueste zuerst.
 *
 * @param context                  Der Next.js Static-Props Kontext.
 * @param context.locale           Die aktuelle Locale.
 * @param context.revalidateReason Der Grund für die Revalidierung.
 * @returns Props mit den Blog-Beiträgen.
 */
export const getStaticProps: GetStaticProps<BlogIndexProps> = async ({locale, revalidateReason}) => {
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config: payloadConfig});
    }

    if (!payload) {
        return {
            props: {
                legalPages: [],
                posts: []
            },
            revalidate: 1
        };
    }

    const payloadLocale = (locale ?? DEFAULT_LOCALE) as Config['locale'];

    const [result, legalPages] = await Promise.all([
        payload.find({
            collection: 'blog-posts',
            depth: 1,
            limit: 0,
            locale: payloadLocale,
            populate: CONTENT_POPULATE,
            select: {
                author: true,
                description: true,
                headerImage: true,
                publishedAt: true,
                slug: true,
                title: true
            },
            sort: '-publishedAt',
            where: {_status: {equals: 'published'}}
        }),
        getLegalPages(payload, payloadLocale)
    ]);

    return {
        props: {
            legalPages,
            posts: JSON.parse(JSON.stringify(result.docs)) as BlogPost[]
        },
        revalidate: REVALIDATE_SECONDS
    };
};

// ── Styled ──────────────────────────────────────────────────

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
    padding: ${spacing(4)} ${spacing(6)};
`;

const Grid = styled.div`
    display: grid;
    gap: ${spacing(6)};
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
`;

const Card = styled.article`
    border: 1px solid ${({theme}) => theme.colors.surfaceHigh};
    border-radius: 12px;
    overflow: hidden;
    transition: box-shadow 0.2s;

    &:hover {
        box-shadow: ${({theme}) => theme.boxShadows.cardShadow};
    }

    a {
        color: inherit;
        text-decoration: none;
    }
`;

const CardImageWrapper = styled.div`
    height: 180px;
    overflow: hidden;
    width: 100%;
`;

const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    padding: ${spacing(4)};
`;

const CardTitle = styled(Headline)`
    margin: 0;
`;

const CardAuthor = styled(P)`
    color: ${({theme}) => theme.colors.inkTertiary};
`;